/* eslint-disable max-lines-per-function */
import { X } from "lucide-react";
import { useEffect, useState, type ReactElement } from "react";
import { Button } from "../../../../shared/components/ui/button";
import { Input } from "../../../../shared/components/ui/input";
import { Label } from "../../../../shared/components/ui/label";
import type { CastMember } from "../types";

interface EditCastModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly titleName: string;
  readonly castData: CastMember[] | null;
  readonly onSave: (castData: CastMember[]) => Promise<void>;
}

/**
 * Modal for editing cast data
 * Allows adding/removing cast members with optional character names
 */
export function EditCastModal({ open, onClose, castData, onSave }: Readonly<EditCastModalProps>): ReactElement | null {
  const [cast, setCast] = useState<CastMember[]>([]);
  const [saving, setSaving] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Cast input state
  const [castName, setCastName] = useState("");
  const [castCharacter, setCastCharacter] = useState("");

  // Initialize from props when modal opens
  useEffect(() => {
    if (open) {
      setCast(castData || []);
      setIsClosing(false);
    }
  }, [open, castData]);

  if (!open && !isClosing) return null;

  const handleClose = () => {
    setIsClosing(true);
    // Wait for animation to complete before actually closing
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 200);
  };

  const handleAddCast = () => {
    if (!castName.trim()) return;
    setCast([...cast, { name: castName.trim(), character: castCharacter.trim() || undefined }]);
    setCastName("");
    setCastCharacter("");
  };

  const handleRemoveCast = (index: number) => {
    setCast(cast.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(cast);
      handleClose();
    } catch (error) {
      console.error("Failed to save cast:", error);
    } finally {
      setSaving(false);
    }
  };

  const isVisible = open && !isClosing;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center ${isVisible ? "animate-in fade-in duration-200" : "animate-out fade-out duration-200"}`}
    >
      {/* Backdrop overlay */}
      <button
        type="button"
        className={`absolute inset-0 bg-black/60 cursor-default ${isVisible ? "animate-in fade-in duration-200" : "animate-out fade-out duration-200"}`}
        onClick={handleClose}
        aria-label="Close modal"
      />

      {/* Modal content */}
      <div
        className={`relative bg-neutral-900/40 backdrop-blur-md border border-neutral-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col ${isVisible ? "animate-in zoom-in-95 fade-in duration-200" : "animate-out zoom-out-95 fade-out duration-200"}`}
      >
        {/* Content - scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          <div>
            <h3 className="text-lg font-medium text-white mb-4">Cast Members</h3>

            {/* Add Cast Form */}
            <div className="grid grid-cols-[1fr,1fr,auto] gap-2 mb-4">
              <div>
                <Label className="text-xs text-neutral-400">Actor Name</Label>
                <Input
                  type="text"
                  placeholder="John Doe"
                  value={castName}
                  onChange={(e) => setCastName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddCast();
                    }
                  }}
                />
              </div>
              <div>
                <Label className="text-xs text-neutral-400">Character (optional)</Label>
                <Input
                  type="text"
                  placeholder="Character Name"
                  value={castCharacter}
                  onChange={(e) => setCastCharacter(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleAddCast();
                    }
                  }}
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  onClick={handleAddCast}
                  disabled={!castName.trim()}
                  className="bg-lumi-accent hover:bg-lumi-accent/50 text-black"
                >
                  Add
                </Button>
              </div>
            </div>

            {/* Cast List */}
            <div className="space-y-2">
              {cast.length === 0 ? (
                <p className="text-sm text-neutral-600 italic py-4">No cast members added yet</p>
              ) : (
                cast.map((member, index) => (
                  <div
                    key={`${member.name}-${index}`}
                    className="flex items-center justify-between bg-black/20 border border-neutral-800 px-4 py-3 rounded-lg hover:border-neutral-700 transition-colors"
                  >
                    <div className="flex-1">
                      <span className="text-sm text-white font-medium">{member.name}</span>
                      {member.character && <span className="text-sm text-neutral-400 ml-3">as {member.character}</span>}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveCast(index)}
                      className="text-neutral-500 hover:text-red-400 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-neutral-800">
          <Button type="button" onClick={handleClose} variant="ghost-sm" disabled={saving}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void handleSave()} disabled={saving} variant="ghost-sm">
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
